import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useLocation, useNavigate } from "react-router-dom";
import { CategoryService } from "../../../services/user/category.services";
import { useSelector } from "react-redux";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";

const CategoryComponent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [categoryData, setCategoryData] = useState([]);
    const [filterText, setFilterText] = useState("");
    const pageNumber = location.state?.pageNumber || 1;
    const [currentPage, setCurrentPage] = useState(pageNumber);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const rowsPerPage = 5;

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                const response = await CategoryService.getAllCategory(selectedStore);
                if (response) {
                    setCategoryData(response);
                }
            }
            catch (error) {
                handleNotify('Error fetching Category', error, error, TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT)
                setCategoryData([]);
            }
        }
        fetchCategory();
    }, [])

    const columns = [
        {
            name: "Name",
            selector: row => row.categoryName,
            sortable: true,
        },
        {
            name: "Category Description",
            selector: row => row.categoryDescription,
            sortable: true,
        },
        {
            name: "Active?",
            selector: row => row.isActive,
            sortable: true,
            cell: row => (
                <button className={`btn btn-light radius-30 px-4 ${row.isActive ? 'text-success' : 'text-danger'}`}>
                    {row.isActive === true ? 'Active' : 'Inactive'}
                </button>
            ),
            center: true,
        },
        {
            name: "Action",
            button: true,
            width: "15%",
            cell: row => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>Edit</button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>Delete</button>
                </div>
            ),
        },
    ];

    const filteredData = categoryData.filter(item =>
        Object.keys(item).some(key =>
            item[key]?.toString().toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const handleSearchChange = (event) => {
        setFilterText(event.target.value)
    };

    // Slice the filtered data for the current page
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    // Calculate total pages
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    // Handle the page change
    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleEditClick = (row) => {
        navigate('/category/edit', { state: { categoryId: row?.categoryId } });
    }
    
    const handleDeleteClick = (item) => {
        setCategoryToDelete(item);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            if (categoryToDelete) {
                await CategoryService.deleteCategory(categoryToDelete.categoryId);
                setCategoryData(prevData => prevData.filter(category => category.categoryId !== categoryToDelete.categoryId));
                handleNotify("Category deleted successfully!", TOASTER_TYPE.SUCCESS, TOASTER_POSITION.TOP_RIGHT);
            }
            setShowModal(false);
        } catch (error) {
            handleNotify(error || 'Error while deleting category.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const handleAddNewClick = () => {
        navigate('/category/create');
    };

    return (
        <>
            <div className="page-wrapper m-0 p-4">
                <div className="page-content">
                    <div className="page-breadcrumb d-flex align-items-center mb-3">
                        <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">
                            Categories
                        </div>
                        <div className="ms-auto">
                            <button className="btn btn-primary" onClick={handleAddNewClick}>New +</button>
                        </div>
                    </div>
                    <div className="card p-4 radius-10 border-top border-0 border-4 border-primary">
                        <div className="card-body">
                            <div className="d-flex justify-content-center flex-column flex-md-row flex-xs-row mb-3">
                                <div className="btn-group me-2 mb-3 mb-md-0 mb-sm-0">
                                    <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                    <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                    <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                    <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                                </div>
                                <div className="d-flex ms-auto align-items-center justify-content-center flex-md-row">
                                    <div className="m-2">
                                        <span className="fw-bold">Search:</span>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search 🔍..."
                                        value={filterText}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                            </div>

                            {currentData && (
                                <div className="table-responsive">
                                    <DataTable
                                        columns={columns}
                                        data={currentData}
                                        fixedHeader
                                        responsive
                                        highlightOnHover
                                        sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                    />
                                </div>
                            )}

                            <div className="d-flex mt-4 justify-content-end">
                                <div className="btn-group">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    >
                                        Prev
                                    </button>
                                    {Array.from({ length: totalPages }, (_, index) => (
                                        <button
                                            key={index}
                                            className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => handlePageChange(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showModal &&
                <DeleteModelPopupComponent
                    show={showModal}
                    onClose={handleModalClose}
                    onConfirm={handleDeleteConfirm}
                    title="Confirm"
                    confirmText="Delete"
                    Message="Are you sure want to delete selected record(s)?"
                />
            }
        </>
    );
};

export default CategoryComponent;
